// The rows the Inventory screens share, and the engine's answers about them.
// The screens never do this arithmetic themselves: they load rows and ask the engine.

import { useMemo } from 'react';
import {
  addDays, buyLines, estimateByMaterial, estimatedUnitCents, jobMaterialVariance, leadTime, lineCents, lineOutstanding, linksByMaterial, materialReadiness, needByMaterial, onOrder, orderBy, orderRequestCosts, orderState, priceSummary, reorderLines, shareOrderLine, STEP, stillToCome,
  stockDraw, supplyDemand, total,
  type BuyLine, type DemandJob, type DemandRow, type InvMaterial, type JobMaterialVariance, type LeadItemIn, type MaterialLink, type MaterialReadiness, type OrderState, type PriceSummary, type Purchase, type ReorderLine, type RequestCost, type SdArrivalIn,
  type SdDemandIn, type SdRow,
} from '@zerogap/engine';
import { deleteRow, insertRow, insertRows, refresh, rpc, toast, updateRow, useRows, type Row } from './api';
import { qty, today } from './format';
import { jobTitle, useAllJobs, type JobSummary } from './jobs';
import { useSession } from './session';

/** Vendors by id, and a name for a material's vendor. */
export function useVendors(): { rows: Row[]; byId: Map<string, Row>; name: (id: string | null | undefined) => string | null; loading: boolean } {
  const vendors = useRows('vendor', { order: ['name'] });
  return useMemo(() => {
    const byId = new Map(vendors.rows.map((v) => [v.id as string, v]));
    return { rows: vendors.rows, byId, name: (id) => (id ? (byId.get(id)?.name as string | undefined) ?? null : null), loading: vendors.loading };
  }, [vendors.rows, vendors.loading]);
}

/** One material on a purchase order, with what the engine works out about it. */
export interface OrderLineView {
  row: Row;
  material: Row;
  /** The sum of the receipts against the line. */
  received: string;
  /** The receipts themselves: stock movements that point at the line. */
  receipts: Row[];
  /** Ordered, not closed short, not yet arrived: what is backordered once something has come. */
  outstanding: string;
  /** The requests this line covers, jobs first. */
  requests: Row[];
  /** request id → how much of it the line covers, and how much of that has arrived. */
  share: Record<string, { qty: string; received: string }>;
  /** Ordered beyond what the requests ask for: it goes to stock. */
  for_stock: string;
  lead_days: number | null;
  /** The typed date, or the order date plus the lead time. Empty until the order is placed, or when the lead time is not known. */
  expected_on: string | null;
  est_cents: number;
  actual_cents: number | null;
}

export interface OrderView {
  po: Row;
  vendor: Row | null;
  state: OrderState;
  lines: OrderLineView[];
  invoices: Row[];
  est_cents: number;
}

export interface InventoryLine extends BuyLine {
  material: Row;
  job: JobSummary;
  jobName: string;
  /** Every request this job has for this material. */
  rows: Row[];
  /** The ones not yet on an order. */
  waiting: Row[];
  /** The ones on an order: which order, and how much of the request has arrived. */
  ordered: { request: Row; order: OrderView; line: OrderLineView; qty: string; received: string }[];
  /** Still to buy, plus ordered and not yet arrived: what the job is waiting on. */
  to_come: string;
  /** Where to buy it, typed on the job's items (an allowance line's vendor page), with the item that typed each. */
  links: MaterialLink[];
  /** What a unit is expected to cost: the material's estimating price, or, for a material with none (an allowance), what the job's estimate says. Null when neither is known. */
  est_unit_cents: number | null;
  /** True when est_unit_cents came from the job's estimate rather than the material. */
  est_from_job: boolean;
}

export interface InventoryData {
  loading: boolean;
  materials: Row[];
  materialById: Map<string, Row>;
  jobs: JobSummary[];
  lines: InventoryLine[];
  orders: OrderView[];
  /** material id → outstanding on placed orders. */
  onOrder: Record<string, string>;
  /** Under safety stock, worked out now. */
  reorders: ReorderLine[];
  /** Reorder requests raised and stored (a card or a bin scanned), not yet on an order. */
  waitingReorders: Row[];
  /** material id → last paid, rolling average and the flag. Only materials that have been bought. */
  prices: Map<string, PriceSummary>;
  history: Row[];
  /** material id → supply against demand, month by month. */
  sd: Map<string, SdRow>;
  /** What each job will still draw, tier by tier: the rows behind the supply-and-demand figures. */
  sdDemand: (SdDemandIn & { jobName: string })[];
  /** What is on its way: the rows behind "on order". */
  sdArrivals: (SdArrivalIn & { number: string | null; order_id: string })[];
  usageMonths: number;
  vendorById: Map<string, Row>;
  /** job id → its estimated materials (the locked revision's once it has one). */
  estimates: Map<string, Record<string, { qty: string; cents: number; typed: boolean }>>;
  /** request id → what it cost, with its share of the order's shipping and tax. */
  requestCosts: Map<string, RequestCost>;
  /** job id → material id → what the job has used. */
  consumed: Record<string, Record<string, number>>;
}

const day = (v: unknown) => (v ? new Date(`${String(v).slice(0, 10)}T12:00:00`).getTime() : Number.MAX_SAFE_INTEGER);

/** The calendar day a moment fell on, here. */
export function localDay(ts: unknown): string {
  const d = new Date(String(ts));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const TABLES = ['purchase_line', 'purchase_order', 'purchase_order_line', 'purchase_invoice', 'material_price_history', 'stock_movement', 'material'];
/** After a write that touches purchasing: every screen that shows it reads again. */
export function refreshInventory(): void {
  refresh(...TABLES);
}

// ---------- pulling early ----------
// One action, offered in two places (the grey line on Purchasing, the job's Materials tab).
// It records an early request against the job for what is still to buy; when the job
// locks, that quantity already counts as covered.

/** Put a committed job's line on purchasing before the job locks. */
export async function pullEarly(l: Pick<InventoryLine, 'job_id' | 'material_id' | 'buy' | 'material'>): Promise<void> {
  if (Number(l.buy) <= 0) return;
  await insertRow('purchase_line', { kind: 'early', material_id: l.material_id, job_id: l.job_id, qty: Number(l.buy), note: 'pulled early, before lock' });
  toast(`${l.material.description}: ${qty(l.buy)} ${l.material.unit ?? ''} pulled in to buy now.`);
}

/** An early request can be undone in one click until it is on an order; after that it is taken off the order first. */
export const pullOpen = (r: Row): boolean => r.kind === 'early' && !r.purchase_order_line_id;

export async function undoPull(l: Pick<InventoryLine, 'rows' | 'material'>): Promise<void> {
  for (const r of l.rows.filter(pullOpen)) await deleteRow('purchase_line', r.id);
  toast(`${l.material.description}: pull undone.`);
}

// ---------- orders ----------

/** Something to put on an order: a stored request (by id), or one to be written (a firm line, a computed reorder). */
export interface OrderRequest {
  purchase_line_id?: string;
  kind?: 'job' | 'reorder';
  job_id?: string | null;
  material_id: string;
  qty?: number;
  est_unit_price?: number | null;
  note?: string;
}

/** Put requests on a vendor's order in one step: a draft to add to, or a new numbered order. Returns the order's id. */
export async function putOnOrder(vendorId: string, requests: OrderRequest[], orderId?: string | null): Promise<string> {
  const id = await rpc<string>('put_on_order', { p_vendor_id: vendorId, p_requests: requests, p_order_id: orderId ?? null });
  refreshInventory();
  return id;
}

const estimate = (m: Row): number | null => (m.price === null || m.price === undefined ? null : Number(m.price));

/** The note a job's request carries to the order: the vendor pages typed on its items, each with the item's number, so the buyer opens them from the order. */
export function linkNote(links: MaterialLink[]): string | undefined {
  return links.length ? links.map((k) => `${k.number}: ${k.url}`).join('\n') : undefined;
}

/** What an order would take from one job line: a firm job's gap (written as a request when it goes on the order), and its requests still waiting. */
export function lineRequests(l: InventoryLine): OrderRequest[] {
  const gap = Number(l.buy);
  const est = l.est_unit_cents === null ? null : l.est_unit_cents / 100;
  return [
    ...(l.tier === 'firm' && gap > 0 ? [{ kind: 'job' as const, job_id: l.job_id, material_id: l.material_id, qty: gap, est_unit_price: est, note: linkNote(l.links) }] : []),
    ...l.waiting.map((r) => ({ purchase_line_id: r.id as string, material_id: l.material_id, est_unit_price: est })),
  ];
}

/** Everything about one material that is ready to go on an order: the firm jobs' lines, what was pulled early, and any reorder. */
export function materialRequests(inv: InventoryData, materialId: string, reorderNote: string): OrderRequest[] {
  const m = inv.materialById.get(materialId);
  if (!m) return [];
  return [
    ...inv.lines.filter((l) => l.material_id === materialId).flatMap(lineRequests),
    ...inv.reorders.filter((r) => r.material_id === materialId).map((r) => ({ kind: 'reorder' as const, material_id: materialId, qty: Number(r.qty), est_unit_price: estimate(m), note: reorderNote })),
    ...inv.waitingReorders.filter((r) => r.material_id === materialId).map((r) => ({ purchase_line_id: r.id as string, material_id: materialId, est_unit_price: estimate(m) })),
  ];
}

/** A vendor's newest draft order: the one a click adds to. */
export function draftFor(inv: InventoryData, vendorId: string | null | undefined): OrderView | undefined {
  return vendorId ? [...inv.orders].reverse().find((o) => o.state === 'draft' && o.po.kind === 'order' && o.po.vendor_id === vendorId) : undefined;
}

export async function takeOffOrder(orderLineId: string): Promise<void> {
  await rpc('take_off_order', { p_order_line_id: orderLineId });
  refreshInventory();
}

export async function deleteDraftOrder(orderId: string): Promise<void> {
  await rpc('delete_draft_order', { p_order_id: orderId });
  refreshInventory();
}

// ---------- receiving ----------
// Every receipt is a stock movement against an order line. The database keeps stock on hand
// in step with the movements, so nothing here writes the balance.

/** What arrived against one order line. A part of it is fine: the rest stays open as backordered. */
export async function receive(line: OrderLineView, amount: number, userId: string | null): Promise<void> {
  if (!(amount > 0)) return;
  await insertRow('stock_movement', { material_id: line.row.material_id, kind: 'receive', qty: amount, reason: 'received', purchase_order_line_id: line.row.id, by_user_id: userId });
  refreshInventory();
}

/** Everything still outstanding on an order has arrived. */
export async function receiveAll(order: OrderView, userId: string | null): Promise<void> {
  const rows = order.lines.filter((l) => Number(l.outstanding) > 0).map((l) => ({ material_id: l.row.material_id, kind: 'receive', qty: Number(l.outstanding), reason: 'received', purchase_order_line_id: l.row.id, by_user_id: userId }));
  await insertRows('stock_movement', rows);
  refreshInventory();
}

/** A receipt entered by mistake: taking it back takes the stock back with it. */
export async function undoReceipt(movementId: string): Promise<void> {
  await deleteRow('stock_movement', movementId);
  refreshInventory();
}

/** The rest of this line is not coming: stop waiting for it. A job that was counting on it shows it to buy again. */
export async function closeShort(line: OrderLineView): Promise<void> {
  await updateRow('purchase_order_line', line.row.id, { closed_short_qty: Number(total([line.row.closed_short_qty, line.outstanding])) });
  refreshInventory();
}

export async function reopenShort(line: OrderLineView): Promise<void> {
  await updateRow('purchase_order_line', line.row.id, { closed_short_qty: 0 });
  refreshInventory();
}

/** Stock in with no order behind it (an in-store buy). Recorded in the same shape as an order, so its price and receipt are kept the same way. */
export async function freeReceipt(vendorId: string | null, lines: { material_id: string; qty: number; unit_price: number | null }[], note: string | null, userId: string | null): Promise<string> {
  const id = await rpc<string>('free_receipt', { p_vendor_id: vendorId, p_lines: lines, p_note: note, p_by: userId });
  refreshInventory();
  return id;
}

/** A free receipt entered by mistake: its stock goes back out, then the record goes. */
export async function deleteReceipt(order: OrderView): Promise<void> {
  if (order.po.kind !== 'receipt') return;
  for (const l of order.lines) for (const r of l.receipts) await deleteRow('stock_movement', r.id);
  await deleteRow('purchase_order', order.po.id);
  refreshInventory();
}

// ---------- kanban cards ----------

/** Another card for this material (another bin). Returns the card's label id. */
export async function addKanbanCard(materialId: string): Promise<string> {
  const id = await rpc<string>('add_kanban_card', { p_material_id: materialId });
  refresh('label');
  return id;
}

/** The card was reached: raise its reorder. */
export async function kanbanRaise(labelId: string): Promise<void> {
  await rpc('kanban_raise', { p_label_id: labelId });
  refresh('label');
  refreshInventory();
}

/** The card goes back in its bin: the order was put away, or (cancel) it was scanned by mistake and its request is withdrawn. */
export async function kanbanReturn(labelId: string, cancel = false): Promise<void> {
  await rpc('kanban_return', { p_label_id: labelId, p_cancel: cancel });
  refresh('label');
  refreshInventory();
}

/** Set the count to what was counted. The database works out the difference and records it with the reason. */
export async function correctCount(materialId: string, counted: number, reason: string, userId: string | null): Promise<number> {
  const delta = await rpc<number>('correct_count', { p_material_id: materialId, p_counted: counted, p_reason: reason, p_by: userId });
  refreshInventory();
  return Number(delta);
}

// ---------- the one hook ----------

export function useInventory(): InventoryData {
  const s = useSession();
  const all = useAllJobs();
  const vendors = useVendors();
  const materials = useRows('material', { order: ['sort'] });
  const requests = useRows('purchase_line', { order: ['created_at'] });
  const orders = useRows('purchase_order', { order: ['created_at'] });
  const orderLines = useRows('purchase_order_line', { order: ['sort'] });
  const invoices = useRows('purchase_invoice', { order: ['created_at'] });
  const revisions = useRows('revision', { order: ['rev_no'] });
  const history = useRows('material_price_history', { order: ['bought_at'] });
  // a firm job's quantities are the ones frozen in its last locked revision, not a live re-resolve
  const latest = useMemo(() => {
    const m = new Map<string, Row>();
    for (const r of revisions.rows) m.set(r.job_id, r);
    return m;
  }, [revisions.rows]);
  const frozen = useRows('resolution', { in: { id: [...latest.values()].map((r) => r.resolution_id as string) } });
  const moves = useRows('stock_movement', { in: { kind: ['consume', 'receive'] } });
  const used = useRows('material_usage_monthly');
  const windows = s.setting<{ price_average_months?: number; usage_months?: number }>('inventory_windows', {});
  const averageMonths = Number(windows?.price_average_months ?? 0);
  const usageMonths = Number(windows?.usage_months ?? 0);
  const loading =
    all.loading || vendors.loading || materials.loading || requests.loading || orders.loading || orderLines.loading || invoices.loading || revisions.loading || frozen.loading || moves.loading || history.loading || used.loading;

  return useMemo(() => {
    const materialById = new Map(materials.rows.map((m) => [m.id as string, m]));
    if (loading) {
      return { loading, materials: materials.rows, materialById, jobs: [], lines: [], orders: [], onOrder: {}, reorders: [], waitingReorders: [], prices: new Map(), history: [], sd: new Map(), sdDemand: [], sdArrivals: [], usageMonths, vendorById: vendors.byId, estimates: new Map(), requestCosts: new Map(), consumed: {} };
    }

    // what has arrived against each order line, and what each job has used
    const arrived = new Map<string, Row[]>();
    const consumed: Record<string, Record<string, number>> = {};
    for (const mv of moves.rows) {
      if (mv.kind === 'receive' && mv.purchase_order_line_id) arrived.set(mv.purchase_order_line_id, [...(arrived.get(mv.purchase_order_line_id) ?? []), mv]);
      if (mv.kind === 'consume' && mv.job_id) {
        const byMat = (consumed[mv.job_id] ??= {});
        byMat[mv.material_id] = (byMat[mv.material_id] ?? 0) + Math.abs(Number(mv.qty));
      }
    }

    // orders, with the engine's reading of each
    const orderViews: OrderView[] = orders.rows.map((po) => {
      const vendor = vendors.byId.get(po.vendor_id) ?? null;
      const lines: OrderLineView[] = orderLines.rows
        .filter((l) => l.purchase_order_id === po.id && materialById.has(l.material_id))
        .map((l) => {
          const material = materialById.get(l.material_id)!;
          const receipts = arrived.get(l.id) ?? [];
          const received = total(receipts.map((r) => r.qty));
          const figures = { qty: l.qty, closed_short_qty: l.closed_short_qty, received };
          // jobs are served before the stock shelf
          const reqs = requests.rows.filter((r) => r.purchase_order_line_id === l.id).sort((a, b) => Number(!a.job_id) - Number(!b.job_id));
          const share = shareOrderLine(figures, reqs.map((r) => ({ id: r.id as string, qty: r.qty })));
          const lead = leadTime(material as { vendor_id: string | null; lead_time_days: number | null }, vendor as { id: string; lead_time_days: number | null } | null);
          const bought = total([l.qty, `-${l.closed_short_qty}`]);
          const spare = total([bought, ...Object.values(share).map((x) => `-${x.qty}`)]);
          return {
            row: l,
            material,
            received,
            receipts,
            outstanding: lineOutstanding(figures).toDecimal(),
            requests: reqs,
            share,
            for_stock: Number(spare) > 0 ? spare : '0',
            lead_days: lead,
            expected_on: l.expected_on ?? (po.placed_at && lead !== null ? addDays(localDay(po.placed_at), lead) : null),
            est_cents: l.est_unit_price === null ? 0 : lineCents(l.qty, l.est_unit_price),
            actual_cents: l.actual_unit_price === null ? null : lineCents(bought, l.actual_unit_price),
          };
        });
      return {
        po,
        vendor,
        lines,
        state: orderState(po as { placed_at: string | null; closed_at: string | null }, lines.map((l) => ({ qty: l.row.qty, closed_short_qty: l.row.closed_short_qty, received: l.received }))),
        invoices: invoices.rows.filter((i) => i.purchase_order_id === po.id),
        est_cents: lines.reduce((n, l) => n + l.est_cents, 0),
      };
    });
    const lineView = new Map<string, { order: OrderView; line: OrderLineView }>();
    for (const o of orderViews) for (const l of o.lines) lineView.set(l.row.id, { order: o, line: l });

    // every job that is still demand: firm, committed or likely, and not yet closed out.
    // A firm job's quantities are the ones frozen in its last locked revision.
    const jobById = new Map(all.jobs.map((j) => [j.job.id as string, j]));
    const estimates = new Map<string, Record<string, { qty: string; cents: number; typed: boolean }>>();
    const jobLinks = new Map<string, Record<string, MaterialLink[]>>();
    const needs: DemandJob[] = all.jobs
      .filter((j) => !j.done)
      .map((j) => {
        const rev = latest.get(j.job.id);
        const chain = rev ? frozen.rows.find((x) => x.id === rev.resolution_id)?.chain : null;
        const groups = j.tier === 'firm' && Array.isArray(chain) ? chain : j.res?.groups ?? [];
        estimates.set(j.job.id, estimateByMaterial(groups));
        jobLinks.set(j.job.id, linksByMaterial(groups));
        return { id: j.job.id, tier: j.tier, order: day(j.opp?.window_start) + new Date(j.job.created_at).getTime() / 1e13, need: needByMaterial(groups) };
      });
    // purchasing looks only at the firm and the committed
    const demand = needs.filter((j) => j.tier === 'firm' || j.tier === 'committed');

    // a request counts for what its order line can still deliver; one not on an order counts in full
    const jobRows = requests.rows.filter((r) => r.job_id);
    const demandRows: DemandRow[] = jobRows.map((r) => {
      const on = r.purchase_order_line_id ? lineView.get(r.purchase_order_line_id)?.line.share[r.id] : null;
      return { job_id: r.job_id, material_id: r.material_id, qty: on ? on.qty : r.qty, received: on ? on.received : 0, early: r.kind === 'early' };
    });

    const lines: InventoryLine[] = buyLines(demand, materials.rows as unknown as InvMaterial[], demandRows, consumed)
      .map((l) => {
        const job = jobById.get(l.job_id)!;
        const rows = jobRows.filter((r) => r.job_id === l.job_id && r.material_id === l.material_id);
        const waiting = rows.filter((r) => !r.purchase_order_line_id);
        const ordered = rows.flatMap((r) => {
          const on = r.purchase_order_line_id ? lineView.get(r.purchase_order_line_id) : null;
          return on ? [{ request: r, order: on.order, line: on.line, qty: on.line.share[r.id]?.qty ?? '0', received: on.line.share[r.id]?.received ?? '0' }] : [];
        });
        const material = materialById.get(l.material_id)!;
        // an allowance (a price typed on the job), or a material with no price of its own, is expected to cost what the job's estimate says
        const est = estimates.get(l.job_id)?.[l.material_id];
        const own = material && !est?.typed ? estimate(material) : null;
        const fromJob = own === null ? estimatedUnitCents(est) : null;
        return {
          ...l,
          material,
          job,
          jobName: jobTitle(job),
          rows,
          waiting,
          ordered,
          to_come: stillToCome(l.buy, [...waiting.map((r) => ({ qty: r.qty, received: 0 })), ...ordered.map((o) => ({ qty: o.qty, received: o.received }))]),
          links: jobLinks.get(l.job_id)?.[l.material_id] ?? [],
          est_unit_cents: own !== null ? Math.round(own * 100) : fromJob,
          est_from_job: own === null && fromJob !== null,
        };
      })
      .filter((l) => l.material);

    const onOrderNow = onOrder(orderViews.map((o) => ({ placed_at: o.po.placed_at, closed_at: o.po.closed_at, lines: o.lines.map((l) => ({ material_id: l.row.material_id, qty: l.row.qty, closed_short_qty: l.row.closed_short_qty, received: l.received })) })));

    // a reorder already asked for (waiting, or sitting on a draft) is not raised a second time;
    // once its order is placed it counts as on order instead
    const asked = requests.rows.filter((r) => r.kind === 'reorder' && (!r.purchase_order_line_id || lineView.get(r.purchase_order_line_id)?.order.state === 'draft'));
    const reorders = reorderLines(materials.rows as unknown as InvMaterial[], onOrderNow, new Set(asked.map((r) => r.material_id as string))).filter((r) => materialById.has(r.material_id));

    const bought = new Map<string, Purchase[]>();
    for (const h of history.rows) bought.set(h.material_id, [...(bought.get(h.material_id) ?? []), { bought_at: localDay(h.bought_at), qty: h.qty, unit_price: h.unit_price, vendor_id: h.vendor_id }]);
    const prices = new Map<string, PriceSummary>();
    for (const [id, list] of bought) if (materialById.has(id)) prices.set(id, priceSummary(list, materialById.get(id)!.price, today(), averageMonths));

    // supply against demand: what each job will still draw, and what is on its way
    const sdDemand = needs.flatMap((j) => {
      const job = jobById.get(j.id)!;
      return Object.entries(j.need).map(([material_id, need]) => ({
        material_id,
        job_id: j.id,
        tier: j.tier,
        qty: total([need as string, `-${Math.min(Number(need), consumed[j.id]?.[material_id] ?? 0)}`]),
        needed_on: job.opp?.window_start ? String(job.opp.window_start).slice(0, 10) : null,
        likelihood_pct: job.opp?.likelihood_pct ?? null,
        jobName: jobTitle(job),
      }));
    });
    const sdArrivals = orderViews
      .filter((o) => o.po.placed_at && !o.po.closed_at)
      .flatMap((o) => o.lines.filter((l) => Number(l.outstanding) > 0).map((l) => ({ material_id: l.row.material_id as string, qty: l.outstanding, expected_on: l.expected_on, number: (o.po.number as string | null) ?? null, order_id: o.po.id as string })));
    const sd = new Map(supplyDemand(materials.rows as unknown as InvMaterial[], sdDemand, sdArrivals, used.rows as { material_id: string; month: string; qty: number }[], today(), usageMonths).map((r) => [r.material_id, r]));

    // what each request cost, with its share of its order's shipping and tax
    const requestCosts = new Map<string, RequestCost>();
    for (const o of orderViews) {
      const costs = orderRequestCosts(
        o.lines.map((l) => ({ id: l.row.id, qty: l.row.qty, closed_short_qty: l.row.closed_short_qty, est_unit_price: l.row.est_unit_price, actual_unit_price: l.row.actual_unit_price, requests: l.requests.map((r) => ({ id: r.id as string, qty: l.share[r.id]?.qty ?? 0 })) })),
        o.invoices.reduce((n, i) => n + Math.round(Number(i.shipping ?? 0) * 100), 0),
        o.invoices.reduce((n, i) => n + Math.round(Number(i.tax ?? 0) * 100), 0),
      );
      for (const [id, c] of Object.entries(costs)) requestCosts.set(id, c);
    }

    return {
      loading: false,
      materials: materials.rows,
      materialById,
      jobs: all.jobs,
      lines,
      orders: orderViews,
      onOrder: onOrderNow,
      reorders,
      waitingReorders: requests.rows.filter((r) => r.kind === 'reorder' && !r.purchase_order_line_id && materialById.has(r.material_id)),
      prices,
      history: history.rows,
      sd,
      sdDemand,
      sdArrivals,
      usageMonths,
      vendorById: vendors.byId,
      estimates,
      requestCosts,
      consumed,
    };
  }, [loading, all.jobs, vendors.byId, materials.rows, requests.rows, orders.rows, orderLines.rows, invoices.rows, latest, frozen.rows, moves.rows, history.rows, used.rows, averageMonths, usageMonths]);
}

// ---------- connections: the job, the shop board, the schedule ----------
// Each is the engine's arithmetic over the rows the hook has already loaded.

/** Estimated against bought, material by material, for one job. Null when the job has no estimate and nothing bought. */
export function jobVariance(inv: InventoryData, jobId: string): JobMaterialVariance | null {
  const estimate = inv.estimates.get(jobId) ?? {};
  const lines = inv.lines.filter((l) => l.job_id === jobId);
  const bought = lines.flatMap((l) => l.ordered.map((o) => ({ material_id: l.material_id, cost: inv.requestCosts.get(o.request.id as string)! })).filter((b) => b.cost));
  if (Object.keys(estimate).length === 0 && bought.length === 0) return null;
  return jobMaterialVariance(
    estimate,
    bought,
    lines.map((l) => ({
      material_id: l.material_id,
      stock_qty: stockDraw(l.consumed, total(l.ordered.map((o) => o.received)), l.from_stock),
      unit_price: l.material.price,
      open_qty: l.to_come,
    })),
  );
}

/** The materials a shop card needs that its job is still waiting on: not yet bought, or bought and not yet in. */
export function cardWaiting(inv: InventoryData, card: Row): { material_id: string; description: string; qty: string; unit: string | null }[] {
  const needs = ((card.bom as Row[] | null) ?? []).filter((x) => x.material_id && Number(x.qty) > 0);
  const out = new Map<string, { material_id: string; description: string; qty: string; unit: string | null }>();
  for (const x of needs) {
    const line = inv.lines.find((l) => l.job_id === card.job_id && l.material_id === x.material_id);
    if (line && Number(line.to_come) > 0) out.set(x.material_id, { material_id: x.material_id, description: String(line.material.description), qty: line.to_come, unit: line.material.unit ?? null });
  }
  return [...out.values()];
}

export interface JobReadiness extends MaterialReadiness {
  /** How many materials are still outstanding for the job. */
  outstanding: number;
  /** The last day to order (for a job not locked yet: to lock) and still have the longest unordered item for the build window. */
  order_by: string | null;
  /** Even ordered today, the materials arrive after the build window starts. */
  late: boolean;
}

/**
 * When a job's materials can all be in hand. A won job is read from its purchasing lines;
 * a job not yet won from what it would need that the shelf does not already hold.
 */
export function jobReadiness(inv: InventoryData, j: JobSummary): JobReadiness {
  const lead = (m: Row) => leadTime(m as { vendor_id: string | null; lead_time_days: number | null }, (inv.vendorById.get(m.vendor_id) as { id: string; lead_time_days: number | null } | undefined) ?? null);
  const items: LeadItemIn[] = [];
  if (j.tier === 'firm' || j.tier === 'committed') {
    for (const l of inv.lines.filter((x) => x.job_id === j.job.id)) {
      if (Number(l.buy) > 0 || l.waiting.length > 0) items.push({ material_id: l.material_id, ordered: false, lead_days: lead(l.material) });
      for (const o of l.ordered) {
        if (Number(o.qty) <= Number(o.received)) continue;
        // on a draft it has not been ordered yet
        if (o.order.state === 'draft') items.push({ material_id: l.material_id, ordered: false, lead_days: o.line.lead_days });
        else items.push({ material_id: l.material_id, ordered: true, expected_on: o.line.expected_on });
      }
    }
  } else if (j.tier === 'likely') {
    for (const d of inv.sdDemand.filter((x) => x.job_id === j.job.id && Number(x.qty) > 0)) {
      const m = inv.materialById.get(d.material_id);
      if (!m || m.tracking_mode === 'bin' || Number(m.stock_on_hand) >= Number(d.qty)) continue;
      items.push({ material_id: d.material_id, ordered: false, lead_days: lead(m) });
    }
  }
  const r = materialReadiness(items, today());
  const start = j.opp?.window_start ? String(j.opp.window_start).slice(0, 10) : null;
  return {
    ...r,
    outstanding: new Set(items.map((i) => i.material_id)).size,
    order_by: start && r.longest_unordered ? orderBy(start, r.longest_unordered.lead_days) : null,
    late: !!(start && r.earliest_start && r.earliest_start > start),
  };
}